import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { closestCenter, DndContext, DragOverlay, KeyboardSensor, PointerSensor, pointerWithin, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import { Button } from "../Button";
import { DropZone } from "../FilterPanel/DropZone";
import { Icon } from "../Icon";
import { useLocalizedStrings } from "../LocalizationProvider";
import { GroupByCondition } from "./GroupByCondition";
function assignIds(rules) {
    return rules.map(rule => ({ ...rule, id: uuidv4() }));
}
function convertToExternal(rules) {
    return rules.map(({ id, ...rule }) => rule);
}
export const GroupByPanelContent = ({ value, onChange, availableFields = [], onConfirm, onClose, className, compact = false, hideSortDirection = false }) => {
    const L = useLocalizedStrings();
    const initialRules = value?.rules ? assignIds(value.rules) : [];
    const [rules, setRules] = useState(initialRules);
    const [initialConfig, setInitialConfig] = useState(() => value || { enabled: false, rules: [] });
    const hasUnsavedChanges = onConfirm && JSON.stringify(convertToExternal(rules)) !== JSON.stringify(initialConfig.rules);
    const [activeId, setActiveId] = useState(null);
    const [overId, setOverId] = useState(null);
    const [isKeyboardDrag, setIsKeyboardDrag] = useState(false);
    const sensors = useSensors(useSensor(PointerSensor, {
        onActivation: () => setIsKeyboardDrag(false)
    }), useSensor(KeyboardSensor, {
        onActivation: () => setIsKeyboardDrag(true)
    }));
    const emitChange = useCallback((newRules) => {
        setRules(newRules);
        if (newRules.length === 0) {
            onChange?.({ enabled: false });
        }
        else {
            const externalRules = convertToExternal(newRules);
            onChange?.({ enabled: true, rules: externalRules });
        }
    }, [onChange]);
    useEffect(() => {
        if (value?.rules) {
            setRules(assignIds(value.rules));
        }
        else {
            setRules([]);
        }
    }, [value]);
    const maxGroupings = 3;
    const hasActiveGroupBy = rules.length > 0;
    const availableFieldsForNew = availableFields.filter(field => !rules.some(rule => rule.field === field.key));
    const canAddMoreGroupings = rules.length < maxGroupings && availableFieldsForNew.length > 0;
    const handleAddCondition = () => {
        if (rules.length >= maxGroupings)
            return;
        const usedFields = new Set(rules.map(rule => rule.field));
        const nextAvailableField = availableFields.find(field => !usedFields.has(field.key));
        if (!nextAvailableField)
            return;
        const newRule = {
            id: uuidv4(),
            field: nextAvailableField.key,
            direction: 'asc'
        };
        emitChange([...rules, newRule]);
    };
    const handleRuleUpdate = (ruleId, updates) => {
        const newRules = rules.map(rule => rule.id === ruleId ? { ...rule, ...updates } : rule);
        emitChange(newRules);
    };
    const handleRuleDelete = (ruleId) => {
        const newRules = rules.filter(rule => rule.id !== ruleId);
        emitChange(newRules);
    };
    const clearGroupBy = () => {
        emitChange([]);
    };
    const customCollisionDetection = (args) => {
        if (isKeyboardDrag) {
            return closestCenter(args);
        }
        else {
            const pointerCollisions = pointerWithin(args);
            if (pointerCollisions.length === 0) {
                return closestCenter(args);
            }
            return pointerCollisions;
        }
    };
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };
    const handleDragOver = (event) => {
        const overId = event.over?.id || null;
        setOverId(overId);
    };
    const handleDragCancel = () => {
        setActiveId(null);
        setOverId(null);
        setIsKeyboardDrag(false);
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        setOverId(null);
        setIsKeyboardDrag(false);
        if (!over || active.id === over.id) {
            return;
        }
        const activeId = active.id;
        const overId = over.id;
        if (overId.includes('-before-') || overId.includes('-after-') || overId.includes('-between-')) {
            let targetRuleId = '';
            let insertPosition = 'before';
            if (overId.includes('-before-')) {
                targetRuleId = overId.split('-before-')[1];
                insertPosition = 'before';
            }
            else if (overId.includes('-after-')) {
                targetRuleId = overId.split('-after-')[1];
                insertPosition = 'after';
            }
            else if (overId.includes('-between-')) {
                const betweenPart = overId.split('-between-')[1];
                const ruleIds = rules.map(r => r.id);
                for (const ruleId of ruleIds) {
                    if (betweenPart.endsWith('-' + ruleId)) {
                        targetRuleId = ruleId;
                        insertPosition = 'before';
                        break;
                    }
                }
            }
            if (targetRuleId) {
                const activeIndex = rules.findIndex(rule => rule.id === activeId);
                const targetIndex = rules.findIndex(rule => rule.id === targetRuleId);
                if (activeIndex !== -1 && targetIndex !== -1) {
                    const newRules = [...rules];
                    const [movedRule] = newRules.splice(activeIndex, 1);
                    let insertIndex = targetIndex;
                    if (activeIndex < targetIndex) {
                        insertIndex = targetIndex - 1;
                    }
                    if (insertPosition === 'after') {
                        insertIndex = insertIndex + 1;
                    }
                    newRules.splice(insertIndex, 0, movedRule);
                    emitChange(newRules);
                }
            }
            return;
        }
        const activeIndex = rules.findIndex(rule => rule.id === activeId);
        const overIndex = rules.findIndex(rule => rule.id === overId);
        if (activeIndex !== -1 && overIndex !== -1) {
            const newRules = arrayMove(rules, activeIndex, overIndex);
            emitChange(newRules);
        }
    };
    const ruleIds = useMemo(() => rules.map(rule => rule.id), [rules]);
    return (_jsxs("div", { className: className, children: [_jsx("div", { className: "relative", children: _jsxs(DndContext, { sensors: sensors, collisionDetection: customCollisionDetection, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragEnd: handleDragEnd, onDragCancel: handleDragCancel, modifiers: [restrictToVerticalAxis], children: [_jsx(SortableContext, { items: ruleIds, strategy: verticalListSortingStrategy, children: hasActiveGroupBy ? (_jsx("div", { className: "", children: rules.map((rule, index) => (_jsxs(React.Fragment, { children: [index === 0 && (_jsx(DropZone, { id: `groupby-before-${rule.id}`, isOver: overId === `groupby-before-${rule.id}` && activeId !== null && activeId !== rule.id, className: "h-1" })), _jsx(GroupByCondition, { rule: rule, availableFields: availableFields, excludeFields: rules.filter(r => r.id !== rule.id).map(r => r.field), onUpdate: (updates) => handleRuleUpdate(rule.id, updates), onDelete: () => handleRuleDelete(rule.id), showOrder: index > 0, orderText: L.component_library.group_by_panel.then, compact: compact, hideSortDirection: hideSortDirection }), index < rules.length - 1 && (_jsx(DropZone, { id: `groupby-between-${rule.id}-${rules[index + 1].id}`, isOver: overId === `groupby-between-${rule.id}-${rules[index + 1].id}` && activeId !== null, className: "h-1" })), index === rules.length - 1 && (_jsx(DropZone, { id: `groupby-after-${rule.id}`, isOver: overId === `groupby-after-${rule.id}` && activeId !== null, className: "h-1" }))] }, rule.id))) })) : (_jsx("div", { className: "text-sm text-gray-500 flex flex-col items-center gap-2 py-8 my-4 border-[1px] border-gray-200 rounded", children: availableFields.length === 0 ? (_jsx("span", { className: "text-center text-base", children: "No available fields to group by. Select some fields first." })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: "text-center text-base", children: "No grouping configured" }), _jsx(Button, { variant: "primary", size: "md", onClick: handleAddCondition, icon: _jsx(Icon, { name: "PlusIcon", type: "solid" }), disabled: !canAddMoreGroupings, title: availableFieldsForNew.length === 0
                                                ? "No fields available for grouping"
                                                : "Select a field to group by", children: L.component_library.group_by_panel.select_field_to_group_by })] })) })) }), _jsx(DragOverlay, { children: activeId ? (() => {
                                const draggedRule = rules.find(rule => rule.id === activeId);
                                if (!draggedRule)
                                    return null;
                                return (_jsx("div", { className: "bg-white border border-blue-500 rounded shadow-lg py-2 px-1 opacity-80", children: _jsx(GroupByCondition, { rule: draggedRule, availableFields: availableFields, excludeFields: rules.filter(r => r.id !== draggedRule.id).map(r => r.field), onUpdate: () => { }, onDelete: undefined, compact: compact, hideSortDirection: hideSortDirection }) }));
                            })() : null })] }) }), _jsx("hr", { className: "mt-0 mb-2" }), _jsxs("div", { className: "flex justify-between space-x-8 items-start items-center", children: [_jsx("div", { className: "flex flex-col justify-start sm:flex-row gap-2", children: availableFields.length === 0 ? (_jsx("span", { className: "text-xs py-3 px-1 text-gray-500", children: "No fields available for grouping" })) : canAddMoreGroupings ? (_jsx(Button, { variant: "secondary", size: "md", onClick: handleAddCondition, icon: _jsx(Icon, { name: "PlusIcon", type: "solid" }), title: rules.length >= maxGroupings
                                ? `Maximum of ${maxGroupings} groupings allowed`
                                : availableFieldsForNew.length === 0
                                    ? "No more fields available"
                                    : "Add another grouping", children: "Add grouping" })) : rules.length >= maxGroupings ? (_jsx("span", { className: "text-xs py-3 px-1", children: "Maximum number of groupings configured" })) : null }), _jsxs("div", { className: "flex flex-col flex-shrink-0 gap-2 items-center justify-end sm:flex-row", children: [hasActiveGroupBy && !onConfirm && (_jsx(Button, { variant: "secondary", size: "md", onClick: clearGroupBy, children: "Clear all" })), onConfirm && (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "secondary", size: "md", onClick: () => {
                                            setRules(assignIds(initialConfig.rules || []));
                                            onClose?.();
                                        }, children: "Cancel" }), _jsx(Button, { size: "md", onClick: () => {
                                            const externalRules = convertToExternal(rules);
                                            const newConfig = { enabled: rules.length > 0, rules: externalRules };
                                            onConfirm(newConfig);
                                            setInitialConfig(newConfig);
                                            onClose?.();
                                        }, children: "Apply" })] }))] })] })] }));
};
